import { Users, TrendingUp, Calendar, CheckCircle } from "lucide-react";

const AdminStats = ({ stats }) => {
  const statCards = [
    {
      title: "Toplam Lead",
      value: stats.total_leads,
      icon: Users,
      color: "from-blue-500 to-blue-600",
      bgColor: "bg-blue-500/10",
      borderColor: "border-blue-500/30",
    },
    {
      title: "Bugün",
      value: stats.today_leads,
      icon: Calendar,
      color: "from-green-500 to-green-600",
      bgColor: "bg-green-500/10",
      borderColor: "border-green-500/30",
    },
    {
      title: "Son 7 Gün",
      value: stats.last_7_days,
      icon: TrendingUp,
      color: "from-purple-500 to-purple-600",
      bgColor: "bg-purple-500/10",
      borderColor: "border-purple-500/30",
    },
    {
      title: "Son 30 Gün",
      value: stats.last_30_days,
      icon: TrendingUp,
      color: "from-orange-500 to-orange-600",
      bgColor: "bg-orange-500/10",
      borderColor: "border-orange-500/30",
    },
  ];

  return (
    <div className="space-y-8" data-testid="admin-stats">
      <h2 className="text-3xl font-black text-gradient">İstatistikler</h2>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {statCards.map((stat, index) => (
          <div
            key={index}
            className="glass-card-premium rounded-2xl p-6 neon-border hover-lift smooth-transition"
            data-testid={`stat-card-${index}`}
          >
            <div className="flex items-center justify-between mb-4">
              <div
                className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${stat.color} flex items-center justify-center shadow-lg neon-glow`}
              >
                <stat.icon className="w-7 h-7 text-white" />
              </div>
            </div>
            <p className="text-sm text-teal-400 mb-2 font-bold uppercase tracking-wider">{stat.title}</p>
            <p className="text-4xl font-black text-white">{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Distribution Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Usage Type Distribution */}
        <div className="glass-card-premium rounded-2xl p-6 neon-border">
          <h3 className="text-xl font-black text-teal-400 mb-5">Kullanım Tipi Dağılımı</h3>
          <div className="space-y-3">
            {Object.entries(stats.usage_type_distribution).map(([type, count]) => (
              <div key={type} className="flex items-center justify-between p-3 bg-gray-900/50 rounded-xl border border-teal-500/20">
                <span className="text-sm text-gray-300 font-medium">{type}</span>
                <span className="text-lg font-bold text-white">{count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* BAC Band Distribution */}
        <div className="glass-card-premium rounded-2xl p-6 neon-border">
          <h3 className="text-xl font-black text-teal-400 mb-5">Promil Bandı Dağılımı</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-900/50 rounded-xl border border-green-500/30">
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 rounded-full bg-green-500 shadow-lg" style={{boxShadow: '0 0 10px rgba(34, 197, 94, 0.8)'}}></div>
                <span className="text-sm text-gray-300 font-medium">0 - 0.39‰ (Güvenli)</span>
              </div>
              <span className="text-lg font-bold text-white">
                {stats.bac_band_distribution["0-0.39"]}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-900/50 rounded-xl border border-yellow-500/30">
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 rounded-full bg-yellow-500 shadow-lg" style={{boxShadow: '0 0 10px rgba(234, 179, 8, 0.8)'}}></div>
                <span className="text-sm text-gray-300 font-medium">0.40 - 0.49‰ (Sınırda)</span>
              </div>
              <span className="text-lg font-bold text-white">
                {stats.bac_band_distribution["0.40-0.49"]}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-900/50 rounded-xl border border-red-500/30">
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 rounded-full bg-red-500 shadow-lg" style={{boxShadow: '0 0 10px rgba(239, 68, 68, 0.8)'}}></div>
                <span className="text-sm text-gray-300 font-medium">0.50+‰ (Yasal Dışı)</span>
              </div>
              <span className="text-lg font-bold text-white">
                {stats.bac_band_distribution["0.50+"]}
              </span>
            </div>
          </div>
        </div>

        {/* Channel Distribution */}
        <div className="glass-card-premium rounded-2xl p-6 neon-border">
          <h3 className="text-xl font-black text-teal-400 mb-5">Kanal Dağılımı</h3>
          <div className="space-y-3">
            {Object.entries(stats.channel_distribution).map(([channel, count]) => (
              <div key={channel} className="flex items-center justify-between p-3 bg-gray-900/50 rounded-xl border border-teal-500/20">
                <span className="text-sm text-gray-300 font-medium capitalize">{channel}</span>
                <span className="text-lg font-bold text-white">{count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Consent Rates */}
        <div className="glass-card-premium rounded-2xl p-6 neon-border">
          <h3 className="text-xl font-black text-teal-400 mb-5">Onay Oranları</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-900/50 rounded-xl border border-teal-500/20">
              <span className="text-sm text-gray-300 font-medium">KVKK Onayı</span>
              <span className="text-2xl font-bold text-teal-400">
                %{stats.kvkk_consent_rate}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-900/50 rounded-xl border border-teal-500/20">
              <span className="text-sm text-gray-300 font-medium">Pazarlama İzni</span>
              <span className="text-2xl font-bold text-teal-400">
                %{stats.marketing_consent_rate}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminStats;